import {Component, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {WorkflowStep} from "../workflow-step";
import {AssetService} from "../../asset/asset.service";
import {AssetSelectionComponent} from "../../asset-select/asset-selection.component";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {CatalogService} from "../../catalog/catalog.service";

export interface AssetLocalizationCode {
  id: string;
  type: string;
  localizationCodeId: string;
  selected: boolean;
}

@Component({
  templateUrl: "./pick-asset-order.template.pug",
  providers: [AssetService],
})
export class PickAssetsOrderComponent extends WorkflowStep {
  @ViewChild(AssetSelectionComponent)
  private assetPickerChild: AssetSelectionComponent;

  private isReady: boolean = false;

  orderId: string = "";
  projectId: string = "";

  selectedAssets: string[] = [];
  selectedAccessories: string[] = [];
  selectedSoftware: string[] = [];
  assetsLocalizationCodes: AssetLocalizationCode[] = [];
  disableStep: boolean = false;

  error: Error;

  assetSelected(event: Event): void {
    this.selectedAssets = event["assetIds"];
    this.selectedAccessories = event["accessoryIds"];
    this.selectedSoftware = event["softwareIds"];
  }

  localizationCodeChange(asset: AssetLocalizationCode): void {
    const index = this.assetsLocalizationCodes.findIndex(a => a.id === asset.id);

    if (index >= 0) {
      if (!asset.selected) {
        this.assetsLocalizationCodes = this.assetsLocalizationCodes.filter(a => a.id !== asset.id);
      } else {
        this.assetsLocalizationCodes[index].localizationCodeId = asset.localizationCodeId;
      }
    } else if (asset.selected) this.assetsLocalizationCodes.push(asset);
  }

  setDirty() {
    this.dirty = true;
  }

  @handleErrors()
  async autoSave(): Promise<boolean> {
    const errorMessage = "The Localization Code in each selected device is required";
    const selectedDevicesWithoutCode = this.assetsLocalizationCodes.filter(it => (
      it.type === "asset" && it.selected && !it.localizationCodeId
    ));
    if (selectedDevicesWithoutCode.length) throw Error(errorMessage);

    const solutionsLocalizationCodes = this.assetsLocalizationCodes.filter(it => (
      it.selected && it.localizationCodeId
    ));

    await this.assetService.updateAssetsForOrder(
      this.orderId,
      this.selectedAssets,
      this.selectedAccessories,
      this.selectedSoftware,
      solutionsLocalizationCodes,
    );

    this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
    this.assetPickerChild.refresh();

    return true;
  }

  @handleErrors()
  async updateWithCatalog() {
    const result = await this.catalogService.updateWithITSMCatalog(this.projectId);
    if (result.status !== "Updated") {
      // display errors from the update process?
      // this.error = new Error(`Error updating with ITSM order catalog: ${result.status}`);
    }
  }

  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.isReady = true;
    this.orderId = workflowTargetInfo.targetId;
    this.projectId = workflowTargetInfo.projectId;
    await this.updateWithCatalog();
    this.loading = false;

    if (this.isLocked) {
      this.disableStep = true;
      return;
    }

    this.assetService.checkIfAssetsAreOrdered(this.orderId)
      .then((res) => {
        if (res.ordered > 0) this.disableStep = true;
        if (res.selected > 0) this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
        this.saveOnContinue = (res.selected === 0);
      });
  }

  constructor(
    public catalogService: CatalogService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public assetService: AssetService,
  ) {
    super(true);
  }
}
